import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Button,
} from "@mui/material";
import axios from "axios";
import Layout from "../../layout";
import {
  RevenueTrendComposedChart,
  CollectionEfficiencyChart,
  BillStatusChart,
  StatCard,
} from "./components/ReportsV2Charts";
import { useNavigate } from "react-router-dom";

const RevenueBillingV2: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(2);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);

  // Filter states
  const [fromDate, setFromDate] = useState("2026-03-01");
  const [toDate, setToDate] = useState("2026-03-27");

  const [data, setData] = useState({
    revenueTrend: [] as any[],
    billStatus: [] as any[],
    collectionEfficiency: [] as any[],
  });

  // Derived stats
  const totalRevenue = data.revenueTrend.reduce(
    (acc, curr) => acc + curr.totalRevenue,
    0,
  );
  const totalBills = data.revenueTrend.reduce(
    (acc, curr) => acc + curr.billCount,
    0,
  );
  const avgCollection =
    data.collectionEfficiency.length > 0
      ? data.collectionEfficiency.reduce(
          (acc, curr) => acc + curr.efficiencyPct,
          0,
        ) / data.collectionEfficiency.length
      : 0;
  const billingPeriods = data.revenueTrend.length;

  const handleTabChange = (_: any, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0) navigate("/reports-v2/overview");
    if (newValue === 1) navigate("/reports-v2/consumption");
    if (newValue === 2) navigate("/reports-v2/revenue-billing");
    if (newValue === 3) navigate("/reports-v2/billing-analytics");
  };

  const fetchReportData = async () => {
    setFetching(true);
    try {
      const revenueQueryParams = `from=${fromDate}&to=${toDate}`;

      const [revRes, statusRes, effRes] = await Promise.all([
        axios.get(
          `https://backend.voltreum.com/api/v2/reports/revenue-trend?${revenueQueryParams}`,
        ),
        axios.get(`https://backend.voltreum.com/api/v2/reports/bill-status`),
        axios.get(
          `https://backend.voltreum.com/api/v2/reports/collection-efficiency?months=12`,
        ),
      ]);

      setData({
        revenueTrend: revRes.data.success ? revRes.data.data : [],
        billStatus: statusRes.data.success ? statusRes.data.data : [],
        collectionEfficiency: effRes.data.success ? effRes.data.data : [],
      });
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchReportData().then(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
          }}
        >
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          p: 0,

          minHeight: "100vh",
          color: "#181B25",
        }}
      >
        <Box sx={{ mb: 3, pt: 0, px: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                color: "rgba(0, 0, 0, 0.6)",
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 600,
                minHeight: "48px",
              },
              "& .Mui-selected": { color: "#1064ED !important" },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1064ED",
                height: "3px",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Consumption" />
            <Tab label="Revenue & Billing" />
            <Tab label="Billing Analytics" />
            <Tab label="Power Quality" />
            <Tab label="Predictive Analytics" />
          </Tabs>
        </Box>

        <Box sx={{ px: 3, pb: 4 }}>
          {/* Header Title */}

          {/* Filter Bar */}
          <Card
            sx={{
              mb: 3,
              backgroundColor: "#FFFFFF",
              borderRadius: "10px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #D3E3FD",
            }}
          >
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    sx={{
                      "& .MuiInputBase-root": { backgroundColor: "#F3F6FC" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    sx={{
                      "& .MuiInputBase-root": { backgroundColor: "#F3F6FC" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={2} md={1}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={fetchReportData}
                    sx={{
                      backgroundColor: "#5E5CE6",
                      color: "#FFF",
                      borderRadius: "6px",
                      textTransform: "none",
                      boxShadow: "none",
                    }}
                  >
                    Apply
                  </Button>
                </Grid>
                <Grid item xs={6} md={1}>
                  {fetching && <CircularProgress size={20} />}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Metric Cards Row */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="TOTAL REVENUE"
                value={`$${(totalRevenue / 1000000).toFixed(2)}M`}
                subValue="TOTAL REVENUE"
                color="#1064ED"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="BILLS GENERATED"
                value={`${(totalBills / 1000).toFixed(1)}K`}
                subValue="BILLS GENERATED"
                color="#7C3AED"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="AVG COLLECTION"
                value={`${avgCollection.toFixed(1)}%`}
                subValue="AVG COLLECTION"
                color="#EF4444"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="BILLING PERIODS"
                value={billingPeriods}
                subValue="BILLING PERIODS"
                color="#10B981"
              />
            </Grid>
          </Grid>

          {/* Monthly Revenue */}
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Card
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "10px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                border: "1px solid #D3E3FD",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(0,0,0,0.7)",
                    mb: 2,
                    fontWeight: 700,
                    fontSize: "16px",
                  }}
                >
                  Monthly Revenue
                </Typography>
                <RevenueTrendComposedChart data={data.revenueTrend} />
              </CardContent>
            </Card>
          </Grid>

          <Grid container spacing={3}>
            {/* Bill Status Breakdown */}
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "16px",
                    }}
                  >
                    Bill Status Breakdown
                  </Typography>
                  <BillStatusChart data={data.billStatus} />
                </CardContent>
              </Card>
            </Grid>

            {/* Collection Efficiency */}
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "16px",
                    }}
                  >
                    Collection Efficiency
                  </Typography>
                  <CollectionEfficiencyChart data={data.collectionEfficiency} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Layout>
  );
};

export default RevenueBillingV2;
